import React from "react";
import { Key, Check, XCircle, Clock, PlusCircle, Loader2 } from "lucide-react";
import MetricCard from "./MetricCard";
import SuccessVsFailureChart from "./SuccessVsFailureChart";
import LanguageDistributionCard from "./LanguageDistributionCard";
import UsageByApiTypeChart from "./UsageByApiTypeChart";
import { useAPIUsageAnalytics } from "../../../../../hooks/useAPIUsageAnalytics";

const APIUsageOverview = ({ userId, overviewData: customOverviewData }) => {
  const { analyticsData, loading } = useAPIUsageAnalytics(userId ? { userId } : undefined);

  // Merge live API backend data with fallback defaults
  const metrics = analyticsData?.metrics || customOverviewData?.metrics;
  const successVsFailure = analyticsData?.totalSuccessVsFailure || customOverviewData?.totalSuccessVsFailure;
  const rawLangDist = analyticsData?.topLanguageDistribution || customOverviewData?.topLanguageDistribution;
  const rawUsageByType = analyticsData?.usageByApiType || customOverviewData?.usageByApiType;

  // Format Top Metrics
  const topMetricsRow1 = [
    {
      id: "total-calls",
      title: "Total API Calls",
      value: metrics?.totalApiCalls?.count != null ? metrics.totalApiCalls.count.toLocaleString() : "300",
      trend: "+12.5%",
      trendLabel: "vs last week",
      icon: Key,
      iconBgColor: "bg-[#FFF6E5]",
      iconColor: "text-[#F59E0B]",
    },
    {
      id: "successful-calls",
      title: "Total successful Calls",
      value: metrics?.totalSuccessfulCalls?.count != null ? metrics.totalSuccessfulCalls.count.toLocaleString() : "289",
      trend: "+0.2%",
      trendLabel: "vs last week",
      icon: Check,
      iconBgColor: "bg-[#E8F8F0]",
      iconColor: "text-[#10B981]",
    },
    {
      id: "failed-calls",
      title: "Total failed Calls",
      value: metrics?.totalFailedCalls?.count != null ? metrics.totalFailedCalls.count.toLocaleString() : "11",
      trend: "+8.2%",
      trendLabel: "vs last week",
      icon: XCircle,
      iconBgColor: "bg-[#FEE2E2]",
      iconColor: "text-[#EF4444]",
    },
    {
      id: "avg-response",
      title: "Avg Response Time",
      value: metrics?.avgResponseTime?.formatted || (metrics?.avgResponseTime?.value != null ? `${metrics.avgResponseTime.value}ms` : "124ms"),
      trend: "-8ms",
      trendLabel: "from last week",
      icon: Clock,
      iconBgColor: "bg-[#FFF6E5]",
      iconColor: "text-[#F59E0B]",
    },
  ];

  const topMetricsRow2 = [
    {
      id: "top-api-type",
      title: "Top API type",
      value: metrics?.topApiType?.name || "",
      trend: "+0.2%",
      trendLabel: "from last week",
      icon: PlusCircle,
      iconBgColor: "bg-[#E8F8F0]",
      iconColor: "text-[#10B981]",
    },
    {
      id: "success-rate",
      title: "Success rate",
      value: metrics?.successRate?.percentage != null ? `${metrics.successRate.percentage}%` : "87.9%",
      trend: "+8.2%",
      trendLabel: "vs last week",
      icon: Check,
      iconBgColor: "bg-[#E8F8F0]",
      iconColor: "text-[#10B981]",
    },
  ];

  // Map Language Distribution items
  const languageDistributionItems = rawLangDist
    ? rawLangDist.map((item, idx) => ({
      id: idx + 1,
      label: item.label || `${item.sourceLang} To ${item.targetLang}`,
      count: item.count || 0,
      percentage: item.percentage || 0,
      badge: `+${Math.round(item.percentage || 5)}%`,
    }))
    : undefined;

  // Map Color scheme for API types
  const API_TYPE_COLORS = {
    "Text-To-Text": "#1C5D42",
    "Text-To-Speech": "#B59928",
    "Speech-To-Text": "#10B981",
    "Speech Translations": "#F97316",
    "Text-To-Speech Translations": "#5A67D8",
    "Speech-To-Text Translation": "#FF5A5F",
  };

  const usageByApiTypeData = rawUsageByType
    ? rawUsageByType.map((item) => ({
      name: item.type,
      value: item.count,
      color: API_TYPE_COLORS[item.type] || "#0E5E44",
    }))
    : undefined;

  return (
    <div className="space-y-6 relative">
      {loading && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-2 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-500 border border-gray-100 shadow-sm">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0E5E44]" />
          Updating...
        </div>
      )}

      {/* Top Metrics Row 1 (4 columns) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {topMetricsRow1.map((metric) => (
          <MetricCard key={metric.id} {...metric} />
        ))}
      </div>

      {/* Top Metrics Row 2 (2 columns) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {topMetricsRow2.map((metric) => (
          <MetricCard key={metric.id} {...metric} />
        ))}
      </div>

      {/* Middle Section: Donut Chart & Top Language Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <SuccessVsFailureChart
          successCount={successVsFailure?.success}
          failureCount={successVsFailure?.failure}
        />
        <LanguageDistributionCard items={languageDistributionItems} />
      </div>

      {/* Bottom Section: Usage By API Type Chart */}
      <UsageByApiTypeChart data={usageByApiTypeData} />
    </div>
  );
};

export default APIUsageOverview;
